import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { adminDeletePackage, adminListPackages, adminSavePackage } from '../api/packagesApi';
import { buildUnlimitedAdminPackage } from './packages/admin-package/buildUnlimitedAdminPackage';
import { ADMIN_PATHS, adminPath, adminPackageEditPath } from '../constants/adminPaths';
import { PageSkeleton } from '../../components/loading/skeletons';
import './PackageManagement.css';

// SVG Icons
const AddIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const EditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const SortIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M7 12h10M11 18h2" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PackageManagement = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [activeTab, setActiveTab] = useState(1);
  const [creatingAdminPackage, setCreatingAdminPackage] = useState(false);
  const { admin } = useAdminAuth();

  useEffect(() => {
    if (admin) {
      fetchPackages();
    }
  }, [admin]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const packages = await adminListPackages();
      const sortedPackages = packages.sort((a, b) => {
        const priceA = parseFloat(a.price?.monthly) || 0;
        const priceB = parseFloat(b.price?.monthly) || 0;
        return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
      });
      setPackages(sortedPackages);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Failed to load packages: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUnlimitedAdminPackage = async () => {
    try {
      setCreatingAdminPackage(true);
      setError(null);
      const created = await adminSavePackage({
        packageData: buildUnlimitedAdminPackage(packages),
      });
      setActiveTab(3);
      await fetchPackages();
      if (created?.package) {
        navigate(adminPackageEditPath(created.package));
      }
    } catch (err) {
      setError(err.message || 'Failed to create admin package');
    } finally {
      setCreatingAdminPackage(false);
    }
  };

  const handleSortChange = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    setPackages(prev => [...prev].sort((a, b) => {
      const priceA = parseFloat(a.price.monthly) || 0;
      const priceB = parseFloat(b.price.monthly) || 0;
      return newOrder === 'asc' ? priceA - priceB : priceB - priceA;
    }));
  };

  const getFilteredPackages = () => {
    if (activeTab === 3) {
      return packages.filter((pkg) => pkg.isAdminPackage);
    }

    const catalogPackages = packages.filter((pkg) => !pkg.isAdminPackage);

    if (activeTab === 0) {
      return catalogPackages;
    }
    if (activeTab === 1) {
      return catalogPackages.filter((pkg) => pkg.isActive);
    }
    if (activeTab === 2) {
      return catalogPackages.filter((pkg) => !pkg.isActive);
    }
    return catalogPackages;
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      await adminDeletePackage({ packageId });
      fetchPackages();
    } catch (err) {
      const message = err.message || 'Failed to delete package';
      if (message.includes('assigned to one or more companies')) {
        const deactivate = window.confirm(
          `${message}\n\nDeactivate this package instead?`
        );
        if (deactivate) {
          try {
            await adminDeletePackage({ packageId, forceDeactivate: true });
            fetchPackages();
            return;
          } catch (deactivateErr) {
            setError(deactivateErr.message || 'Failed to deactivate package');
            return;
          }
        }
      }
      console.error('Error deleting package:', err);
      setError(message);
    }
  };

  if (loading) {
    return (
      <div className="admin-page pkg-page">
        <PageSkeleton variant="cards" label="Loading packages" />
      </div>
    );
  }

  const isSuperAdmin = admin?.role === 'super-admin';
  const filteredPackages = getFilteredPackages();
  const catalogPackages = packages.filter((pkg) => !pkg.isAdminPackage);
  const adminPackages = packages.filter((pkg) => pkg.isAdminPackage);
  const allCount = catalogPackages.length;
  const activeCount = catalogPackages.filter((pkg) => pkg.isActive).length;
  const inactiveCount = catalogPackages.filter((pkg) => !pkg.isActive).length;
  const adminCount = adminPackages.length;
  const isAdminTab = activeTab === 3;

  return (
    <div className="admin-page pkg-page">
      <div className="pkg-header">
        <h1>Package Management</h1>
        <p>Manage subscription packages and pricing</p>
      </div>

      {/* Status Tabs */}
      <div className="pkg-tabs">
        <button
          className={`pkg-tab ${activeTab === 0 ? 'pkg-tab-active' : ''}`}
          onClick={() => setActiveTab(0)}
        >
          All ({allCount})
        </button>
        <button
          className={`pkg-tab ${activeTab === 1 ? 'pkg-tab-active' : ''}`}
          onClick={() => setActiveTab(1)}
        >
          Active ({activeCount})
        </button>
        <button
          className={`pkg-tab ${activeTab === 2 ? 'pkg-tab-active' : ''}`}
          onClick={() => setActiveTab(2)}
        >
          Inactive ({inactiveCount})
        </button>
        {isSuperAdmin && (
          <button
            className={`pkg-tab ${activeTab === 3 ? 'pkg-tab-active' : ''}`}
            onClick={() => setActiveTab(3)}
          >
            Admin ({adminCount})
          </button>
        )}
      </div>

      {/* Action Bar */}
      <div className="pkg-action-bar">
        <div className="pkg-count">
          {filteredPackages.length} package{filteredPackages.length !== 1 ? 's' : ''} shown
          {isAdminTab ? ' · internal admin packages' : ''}
        </div>
        <div className="pkg-actions">
          {!isAdminTab && (
            <button className="pkg-btn pkg-btn-secondary" onClick={handleSortChange}>
              <SortIcon />
              Sort by Price ({sortOrder === 'asc' ? 'Low to High' : 'High to Low'})
            </button>
          )}
          {isSuperAdmin && (
            <button
              className="pkg-btn pkg-btn-secondary"
              onClick={handleCreateUnlimitedAdminPackage}
              disabled={creatingAdminPackage}
            >
              <AddIcon />
              {creatingAdminPackage ? 'Creating…' : 'Create unlimited admin package'}
            </button>
          )}
          <button
            className="pkg-btn pkg-btn-primary"
            onClick={() => navigate(adminPath(ADMIN_PATHS.PACKAGES_NEW))}
          >
            <AddIcon />
            Add Package
          </button>
        </div>
      </div>

      {error && (
        <div className="pkg-alert pkg-alert-error">
          <span>âš ï¸</span>
          {error}
        </div>
      )}

      {/* Package Grid */}
      <div className="pkg-grid">
        {filteredPackages.length === 0 ? (
          <p className="pkg-empty-state">
            {isAdminTab
              ? 'No internal admin packages yet. Use Create unlimited admin package to add one with every feature.'
              : 'No packages in this tab.'}
          </p>
        ) : null}
        {filteredPackages.map((pkg) => (
          <div
            key={pkg.id}
            className="pkg-card pkg-card--clickable"
            role="button"
            tabIndex={0}
            onClick={() => navigate(adminPackageEditPath(pkg))}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navigate(adminPackageEditPath(pkg));
              }
            }}
            aria-label={`Edit ${pkg.name} package`}
          >
            <div className="pkg-card-content">
              {/* Package Header */}
              <div className="pkg-card-header">
                <h2 className="pkg-card-title">{pkg.name}</h2>
                <div className="pkg-card-badges">
                  {pkg.isAdminPackage && (
                    <span className="pkg-badge pkg-badge-admin">Admin</span>
                  )}
                  <span className={`pkg-badge ${pkg.isActive ? 'pkg-badge-active' : 'pkg-badge-inactive'}`}>
                    {pkg.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="pkg-price">
                {pkg.isAdminPackage ? (
                  <>
                    Internal
                    <span className="pkg-price-unit"> · permanent access</span>
                  </>
                ) : (
                  <>
                    ${pkg.price.monthly}
                    <span className="pkg-price-unit">/month</span>
                  </>
                )}
              </div>
              
              {/* Description */}
              <p className="pkg-description">{pkg.description}</p>

              <hr className="pkg-divider" />

              {/* Resource Limits */}
              <div style={{ marginBottom: '24px' }}>
                <h3 className="pkg-section-title">Resource Limits</h3>
                <div>
                  {Object.entries(pkg.limits)
                    .filter(([key]) => key === 'employees')
                    .map(([key, value]) => (
                    <div key={key} className="pkg-limit-item">
                      <CheckIcon className="pkg-limit-icon" />
                      <span className="pkg-limit-text">
                        {key.charAt(0).toUpperCase() + key.slice(1)}: 
                        <span className="pkg-limit-value">
                          {value === -1 ? 'Unlimited' : value}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div>
                <h3 className="pkg-section-title">Features</h3>
                <div className="pkg-features">
                  {(() => {
                    if (Array.isArray(pkg.features)) {
                      return pkg.features.map((feature, index) => (
                        <span key={index} className="pkg-feature-chip">
                          {feature}
                        </span>
                      ));
                    } else if (pkg.features && typeof pkg.features === 'object') {
                      return Object.entries(pkg.features).map(([key, enabled]) => (
                        <span key={key} className="pkg-feature-chip">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      ));
                    } else {
                      return (
                        <span className="pkg-feature-chip">No features</span>
                      );
                    }
                  })()}
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="pkg-card-actions">
              <button
                type="button"
                className="pkg-btn-icon pkg-btn-icon-edit"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(adminPackageEditPath(pkg));
                }}
                title="Edit Package"
              >
                <EditIcon />
              </button>
              <button
                type="button"
                className="pkg-btn-icon pkg-btn-icon-danger"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDelete(pkg.id);
                }}
                title="Delete Package"
              >
                <DeleteIcon />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PackageManagement;
